import { useState, useEffect, FormEvent } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Building2,
  User,
  Mail,
  Globe,
  MessageSquare,
  Hash,
} from 'lucide-react'
import api from '../services/api'
import PhoneInput from '../components/PhoneInput'
import logoEspaceClientTexte from '../images/Logo-Espace-client-texte.png'
import logoEspaceClientTexteDark from '../images/Logo-Espace-client-texte-dark.png'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Plan {
  id: number
  name: string
  description: string
  base_price: number
  max_users: number
  max_clients: number
  max_signatures: number
  max_ai_tokens: number
  max_emails: number
  is_custom: boolean
}

interface RegistrationForm {
  company_name: string
  siren: string
  lastname: string
  firstname: string
  email: string
  phone: string
  erp_type: string
  comment: string
}

const ERP_TYPES = ['dolibarr', 'odoo', 'salesforce', 'business_central', 'sage', 'csv', 'autre']

// ── Stripe payment inner form ─────────────────────────────────────────────────

interface PaymentFormProps {
  onSuccess: (piId: string) => void
  onBack: () => void
  amountHT: number
  vatRate: number
  amountTTC: number
  planName: string
}

function StripePaymentForm({
  onSuccess,
  onBack,
  amountHT,
  vatRate,
  amountTTC,
  planName,
}: PaymentFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!stripe || !elements) return

    setLoading(true)
    setError(null)

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    })

    if (stripeError) {
      setError(stripeError.message ?? 'Erreur de paiement')
      setLoading(false)
    } else if (paymentIntent) {
      onSuccess(paymentIntent.id)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Order summary */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-xl p-4 space-y-1">
        <p className="text-sm font-medium text-purple-800 dark:text-purple-200">
          Plan <strong>{planName}</strong>
        </p>
        <div className="text-xs text-purple-600 dark:text-purple-400 space-y-0.5 pt-1">
          <div className="flex justify-between">
            <span>Montant HT</span>
            <span>{amountHT.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
          </div>
          <div className="flex justify-between">
            <span>TVA ({vatRate}%)</span>
            <span>
              {(amountTTC - amountHT).toLocaleString('fr-FR', {
                style: 'currency',
                currency: 'EUR',
              })}
            </span>
          </div>
          <div className="flex justify-between font-bold text-purple-900 dark:text-purple-100 text-sm border-t border-purple-200 dark:border-purple-700 pt-1 mt-1">
            <span>Total TTC / mois</span>
            <span>{amountTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</span>
          </div>
        </div>
      </div>

      <PaymentElement />

      {error && (
        <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition-colors"
        >
          <ArrowLeft size={15} /> Retour
        </button>
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
        >
          {loading && <Loader2 size={15} className="animate-spin" />}
          Payer {amountTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })} TTC
        </button>
      </div>
    </form>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Step = 'form' | 'payment' | 'success'

export default function Subscribe() {
  const [searchParams] = useSearchParams()
  const planIdParam = parseInt(searchParams.get('plan_id') ?? '0', 10)
  const qtyParam = parseInt(searchParams.get('qty') ?? '1', 10)

  const [plans, setPlans] = useState<Plan[]>([])
  const [plan, setPlan] = useState<Plan | null>(null)
  const [quantity] = useState(Math.max(1, qtyParam))
  const [step, setStep] = useState<Step>('form')

  // Payment state (after Step 1 backend call)
  const [clientSecret, setClientSecret] = useState('')
  const [publishableKey, setPublishableKey] = useState('')
  const [requestId, setRequestId] = useState(0)
  const [amountHT, setAmountHT] = useState(0)
  const [vatRate, setVatRate] = useState(20)
  const [amountTTC, setAmountTTC] = useState(0)

  // Form state
  const [form, setForm] = useState<RegistrationForm>({
    company_name: '',
    siren: '',
    lastname: '',
    firstname: '',
    email: '',
    phone: '',
    erp_type: '',
    comment: searchParams.get('comment') ?? '',
  })

  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Load plans on mount
  useEffect(() => {
    api.getPublicPricingPlans().then((data) => {
      setPlans(data)
      const found = data.find((p: Plan) => p.id === planIdParam && !p.is_custom)
      if (found) setPlan(found)
    })
  }, [planIdParam])

  const totalEur = plan ? plan.base_price * quantity : 0

  // ── Step 1 submit ──────────────────────────────────────────────────────────

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!plan) return

    setFormError(null)
    setSubmitting(true)
    try {
      const res = await api.initiatePublicSubscription({
        plan_id: plan.id,
        quantity,
        company_name: form.company_name,
        siren: form.siren,
        lastname: form.lastname,
        firstname: form.firstname,
        email: form.email,
        phone: form.phone,
        erp_type: form.erp_type,
        comment: form.comment,
      })

      setClientSecret(res.client_secret)
      setPublishableKey(res.publishable_key)
      setRequestId(res.request_id)
      setAmountHT(res.amount_ht ?? res.amount_eur)
      setVatRate(res.vat_rate ?? 20)
      setAmountTTC(res.amount_ttc ?? res.amount_eur)
      setStep('payment')
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: { error?: string } } }
      setFormError(apiErr?.response?.data?.error ?? 'Erreur lors de la création du paiement.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Step 2 payment success ─────────────────────────────────────────────────

  const handlePaymentSuccess = async (piId: string) => {
    try {
      await api.confirmPublicSubscription(requestId, piId)
    } catch {
      // best-effort — the backend will reconcile via webhook
    }
    setStep('success')
  }

  // ── Stripe Elements provider ───────────────────────────────────────────────

  const stripePromise = publishableKey ? loadStripe(publishableKey) : null

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            aria-label="Retour à l'accueil"
            title="Retour à l'accueil"
            className="group inline-flex items-center rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            <img
              src={logoEspaceClientTexte}
              alt="Espace Client"
              className="h-8 w-auto transition-transform group-hover:scale-105 dark:hidden"
            />
            <img
              src={logoEspaceClientTexteDark}
              alt=""
              aria-hidden="true"
              className="hidden h-8 w-auto transition-transform group-hover:scale-105 dark:block"
            />
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            ← Retour à l'accueil
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-12">
        {/* Progress indicator */}
        {step !== 'success' && (
          <div className="flex items-center gap-3 mb-8 justify-center">
            {(['form', 'payment'] as const).map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                  ${step === s ? 'bg-purple-600 text-white' : i < ['form', 'payment'].indexOf(step) ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}
                >
                  {i < ['form', 'payment'].indexOf(step) ? <CheckCircle size={16} /> : i + 1}
                </div>
                <span
                  className={`text-sm font-medium hidden sm:block ${step === s ? 'text-purple-700 dark:text-purple-300' : 'text-gray-400'}`}
                >
                  {s === 'form' ? 'Vos informations' : 'Paiement'}
                </span>
                {i < 1 && <div className="w-8 h-0.5 bg-gray-200 dark:bg-gray-700" />}
              </div>
            ))}
          </div>
        )}

        {/* Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-800 p-8">
          {/* ── Step: form ─────────────────────────────────────────── */}
          {step === 'form' && (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold">Créer votre compte fournisseur</h1>
                {plan ? (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Plan sélectionné :{' '}
                    <strong className="text-purple-700 dark:text-purple-300">{plan.name}</strong>
                    {' — '}
                    {plan.base_price.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                    /mois
                    {quantity > 1 &&
                      ` × ${quantity} = ${totalEur.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}/mois`}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">Chargement du plan…</p>
                )}
              </div>

              {/* Plan selector */}
              {plans.filter((p) => !p.is_custom).length > 1 && (
                <div className="mb-5">
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">
                    Plan
                  </label>
                  <select
                    value={plan?.id ?? ''}
                    onChange={(e) =>
                      setPlan(plans.find((p) => p.id === Number(e.target.value)) ?? null)
                    }
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">Choisir un plan…</option>
                    {plans
                      .filter((p) => !p.is_custom)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} —{' '}
                          {p.base_price.toLocaleString('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                          })}
                          /mois
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Company info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      <Building2 size={12} className="inline mr-1" /> Raison sociale *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.company_name}
                      onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))}
                      placeholder="Nom de votre entreprise"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      <Hash size={12} className="inline mr-1" /> SIREN
                    </label>
                    <input
                      type="text"
                      maxLength={14}
                      value={form.siren}
                      onChange={(e) => setForm((f) => ({ ...f, siren: e.target.value }))}
                      placeholder="123 456 789"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      <Globe size={12} className="inline mr-1" /> Type d'ERP *
                    </label>
                    <select
                      required
                      value={form.erp_type}
                      onChange={(e) => setForm((f) => ({ ...f, erp_type: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="">Sélectionner…</option>
                      {ERP_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t.charAt(0).toUpperCase() + t.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Contact */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      <User size={12} className="inline mr-1" /> Prénom *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.firstname}
                      onChange={(e) => setForm((f) => ({ ...f, firstname: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Nom *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.lastname}
                      onChange={(e) => setForm((f) => ({ ...f, lastname: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      <Mail size={12} className="inline mr-1" /> E-mail *
                    </label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <PhoneInput
                      value={form.phone}
                      onChange={(phone) => setForm((f) => ({ ...f, phone }))}
                      label="Téléphone"
                      labelCls="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1"
                      fieldCls="px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    <MessageSquare size={12} className="inline mr-1" /> Commentaire
                  </label>
                  <textarea
                    rows={3}
                    value={form.comment}
                    onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
                    placeholder="Informations complémentaires…"
                    className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !plan}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-50"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  Continuer vers le paiement
                  {!submitting && <ArrowRight size={16} />}
                </button>

                <p className="text-center text-xs text-gray-400 dark:text-gray-500">
                  Déjà client ?{' '}
                  <Link
                    to="/login"
                    className="text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    Se connecter
                  </Link>
                </p>
              </form>
            </>
          )}

          {/* ── Step: payment ──────────────────────────────────────── */}
          {step === 'payment' && clientSecret && stripePromise && (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold">Paiement</h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Votre premier mois sera débité maintenant. Vous pourrez annuler à tout moment.
                </p>
              </div>

              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: { theme: 'stripe' },
                }}
              >
                <StripePaymentForm
                  onSuccess={handlePaymentSuccess}
                  onBack={() => setStep('form')}
                  amountHT={amountHT}
                  vatRate={vatRate}
                  amountTTC={amountTTC}
                  planName={plan?.name ?? ''}
                />
              </Elements>
            </>
          )}

          {/* ── Step: success ───────────────────────────────────────── */}
          {step === 'success' && (
            <div className="text-center py-4">
              <div className="flex justify-center mb-5">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle size={36} className="text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
              <h1 className="text-2xl font-bold mb-2">Paiement confirmé !</h1>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                Votre demande d'inscription a été enregistrée. Notre équipe va l'examiner et vous
                contacter sous 24–48 h pour finaliser la configuration de votre espace client.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl px-6 py-4 text-sm text-gray-600 dark:text-gray-300 mb-6 text-left space-y-1">
                <p>
                  <strong>Plan :</strong> {plan?.name}
                </p>
                <p>
                  <strong>Montant HT :</strong>{' '}
                  {amountHT.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </p>
                <p>
                  <strong>TVA ({vatRate}%) :</strong>{' '}
                  {(amountTTC - amountHT).toLocaleString('fr-FR', {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </p>
                <p>
                  <strong>Montant TTC débité :</strong>{' '}
                  {amountTTC.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}
                </p>
                <p>
                  <strong>E-mail :</strong> {form.email}
                </p>
              </div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl transition-colors"
              >
                Retour à l'accueil
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
